/**
 * Character asset loader for Constructo.
 * Loads a rigged character GLB, sets up AnimationMixer,
 * and exposes playWalk / playIdle for movement-driven animation.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export interface CharacterModel {
  /** The root scene group from the GLB. */
  scene: THREE.Group;
  /** AnimationMixer bound to the loaded scene. */
  mixer: THREE.AnimationMixer;
  /** Playable actions keyed by name. */
  actions: Map<string, THREE.AnimationAction>;
  /** Currently playing action, if any. */
  current: THREE.AnimationAction | null;
}

/**
 * Load a character GLB from the given URL.
 * The GLB must contain an armature with at least "idle" and "walk" animation
 * clips.  Returns a CharacterModel ready for per-frame mixer.update(delta).
 */
export async function loadCharacter(url: string): Promise<CharacterModel> {
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(url);

  const mixer = new THREE.AnimationMixer(gltf.scene);
  const actions = new Map<string, THREE.AnimationAction>();

  for (const clip of gltf.animations) {
    const action = mixer.clipAction(clip);
    action.setLoop(THREE.LoopRepeat, Infinity);
    actions.set(clip.name, action);
  }

  // Default: play idle at startup
  const idle = actions.get('idle');
  if (idle) {
    idle.play();
  }

  return {
    scene: gltf.scene as THREE.Group,
    mixer,
    actions,
    current: idle ?? null,
  };
}

/**
 * Transition the character to the walk animation.
 * Crossfades from the current action to the walk clip.
 * No-op if already walking or walk clip is missing.
 */
export function playWalk(model: CharacterModel, fadeTime = 0.15): void {
  const walk = model.actions.get('walk');
  if (!walk || model.current === walk) return;

  if (model.current) {
    walk.reset().play();
    walk.crossFadeFrom(model.current, fadeTime, false);
  } else {
    walk.play();
  }
  model.current = walk;
}

/**
 * Transition the character to the idle animation.
 * Crossfades from the current action to the idle clip.
 * No-op if already idle or idle clip is missing.
 */
export function playIdle(model: CharacterModel, fadeTime = 0.25): void {
  const idle = model.actions.get('idle');
  if (!idle || model.current === idle) return;

  if (model.current) {
    idle.reset().play();
    idle.crossFadeFrom(model.current, fadeTime, false);
  } else {
    idle.play();
  }
  model.current = idle;
}